// Niveau 2 — Hyppige bogstaver (ø n m v b y)

import type { CurriculumTier, Trin } from '../../types';
import { NIVEAU_1_SPRINT_WORDS, NIVEAU_1_UNLOCKED_KEYS } from './niveau-1';
import { filterWords, interleaved, round } from './generators';
import { consolidationTrin, pairTrin, singleKeyTrin } from './trinBuilders';

let prior = [...NIVEAU_1_UNLOCKED_KEYS];

const t1 = singleKeyTrin(
  'niveau-2',
  1,
  'Ø',
  'Ø ved lillefingeren på øverste række.',
  'Stræk lillefingeren til Ø uden at flytte håndfladen.',
  'ø',
  prior,
  {
    intro: 'ø ø lø øl ø ø lø øl ø ø',
    connect: 'lø æø øl læ øæ lø æø øl læ',
    recall1: 'sø dø fø jø kø lø hø gø',
    recall2: 'fø sø dø jø kø lø hø gø æø',
    challenge: 'sø dø fø jø kø lø hø gø æø',
  },
  { palette: 'echo' },
);
prior = t1.unlockedKeys;

const t2 = pairTrin(
  'niveau-2',
  2,
  'N og M',
  'Hyppige bogstaver på nederste række.',
  'Ram N og M med pegefingrene.',
  ['n', 'm'],
  prior,
  {
    intro: 'n m n m nm mn n m n m nm mn',
    connect: 'fn jm dn km fn jm dn km fn jm',
    recall1: 'nm fn jm dn km nm fn jm dn',
    recall2: 'mn fn jm dn km mn fn jm dn',
    challenge: 'fnjm dnkm nmfj mndk',
  },
  { palette: 'echo' },
);
prior = t2.unlockedKeys;

const t3 = pairTrin(
  'niveau-2',
  3,
  'V og B',
  'Stræk pegefingeren ned til V og B.',
  'Ram V og B uden at løfte hænderne.',
  ['v', 'b'],
  prior,
  {
    intro: 'v b v b vb bv v b v b vb bv',
    connect: 'fv jb dv kb fv jb dv kb fv jb',
    recall1: 'vb fv jb dv kb vb fv jb dv',
    recall2: 'bv fv jb dv kb bv fv jb dv',
    challenge: 'fvjb dvkb vbvf jbdv',
  },
  { palette: 'echo' },
);
prior = t3.unlockedKeys;

const t4 = singleKeyTrin(
  'niveau-2',
  4,
  'Y',
  'Y med højre pegefinger.',
  'Ram Y med højre pegefinger på øverste række.',
  'y',
  prior,
  {
    intro: 'y y jy fy ky y y jy fy ky',
    connect: 'jy fy ky ry ty jy fy ky ry ty',
    recall1: 'jy fy ky ry ty jy fy ky ry',
    recall2: 'yj fy ky ry ty yj fy ky ry',
    challenge: 'jyfy kyry tyjy fyky',
  },
  { palette: 'echo' },
);
prior = t4.unlockedKeys;

const NIVEAU_2_WORD_POOL = [
  'en', 'er', 'det', 'men', 'ny', 'by', 'ven', 'brev',
  'øve', 'søvn', 'bøn', 'møde', 'venner', 'barn', 'far', 'syv',
  'bly', 'rytme', 'nem', 'rim', 'sin', 'min', 'ben', 'reb',
  'vær', 'lyst', 'mørk', 'den', 'gerne', 'selv', 'blive', 'vende',
  'melde', 'bryde', 'øvet', 'ører', 'byde', 'brevet', 'barnet', 'benet',
  'synger', 'fyret', 'mere', 'føler', 'møbel', 'venlig', 'tysk', 'revy',
  'mønt', 'byen', 'nemt', 'rent', 'send', 'vend', 'bland', 'blandt',
  'mener', 'venter', 'ventet', 'føle', 'følte', 'følge', 'følger', 'ønske',
  'ønsker',
  // Navne (dictation-names.json)
  'agnes', 'bjarne', 'bjørn', 'børge', 'ejner', 'emma', 'esben', 'gry', 'hanne',
  'inge', 'jørgen', 'kirsten', 'lene', 'liv', 'mads', 'mette',
  'mia', 'nanna', 'niels', 'øjvind', 'pil', 'sanne', 'søren', 'stig', 'svend',
  'trine', 'vibeke',
];

export const NIVEAU_2_SPRINT_WORDS = filterWords(NIVEAU_2_WORD_POOL, prior);

const t5Base = consolidationTrin(
  'niveau-2',
  5,
  'Konsolidering',
  'Bland Ø, N, M, V, B og Y i ord.',
  'Skriv flydende med de hyppige bogstaver du har lært.',
  prior,
  NIVEAU_2_SPRINT_WORDS,
  'en ny ven er her',
);

const t5: Trin = {
  ...t5Base,
  rounds: [
    ...t5Base.rounds,
    round({
      id: 'niveau-2-trin-5-r7',
      title: 'Bland med Niveau 1',
      purpose: 'Hold gammelt stof varmt. Tegn fra både Niveau 1 og 2.',
      mode: 'recall',
      generate: interleaved(
        [
          { chunks: NIVEAU_1_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_2_SPRINT_WORDS, weight: 2 },
        ],
        90,
      ),
    }),
  ],
};

export const NIVEAU_2_UNLOCKED_KEYS = prior;

export const NIVEAU_2_TRINS: Trin[] = [t1, t2, t3, t4, t5];

export const NIVEAU_2_TIER: CurriculumTier = {
  id: 'niveau-2',
  title: 'Niveau 2',
  description: 'Hyppige bogstaver: Ø, N, M, V, B og Y.',
  trins: NIVEAU_2_TRINS,
};
